import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Activity, TrendingUp, AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_STYLES = {
  A: { bg: 'from-emerald-500 to-green-600', ring: 'ring-emerald-300', text: 'text-emerald-700', light: 'bg-emerald-50' },
  B: { bg: 'from-green-500 to-lime-600', ring: 'ring-green-300', text: 'text-green-700', light: 'bg-green-50' },
  C: { bg: 'from-yellow-500 to-amber-600', ring: 'ring-yellow-300', text: 'text-yellow-700', light: 'bg-yellow-50' },
  D: { bg: 'from-orange-500 to-orange-600', ring: 'ring-orange-300', text: 'text-orange-700', light: 'bg-orange-50' },
  E: { bg: 'from-red-500 to-red-600', ring: 'ring-red-300', text: 'text-red-700', light: 'bg-red-50' },
  F: { bg: 'from-red-700 to-red-900', ring: 'ring-red-400', text: 'text-red-800', light: 'bg-red-100' }
};

const BAR_COLORS = {
  A: 'bg-emerald-500', B: 'bg-green-500', C: 'bg-yellow-500',
  D: 'bg-orange-500', E: 'bg-red-500', F: 'bg-red-800'
};

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function CodeQualityScoreWidget({ onViewDetails }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchScore = async (force = false) => {
    setRefreshing(force);
    if (!force) setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/code-quality-score${force ? '?no_cache=true' : ''}`,
        { headers: getAuthHeader() }
      );
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchScore(false); }, []);

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200" data-testid="code-quality-widget-loading">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-slate-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span className="text-sm">Analyse de la qualité du code...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-red-50 border-red-200" data-testid="code-quality-widget-error">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <span className="text-sm font-medium">Code Quality indisponible : {error || 'Aucune donnée'}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const style = GRADE_STYLES[data.letter] || GRADE_STYLES.C;
  const totalFns = data.kpis.total_functions || 1;

  return (
    <Card className="overflow-hidden border-slate-200 hover:shadow-lg transition-all" data-testid="code-quality-widget">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className={`lg:col-span-3 bg-gradient-to-br ${style.bg} text-white p-6 flex flex-col items-center justify-center text-center`}>
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <Activity className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Code Quality</span>
            </div>
            <div className={`w-24 h-24 rounded-full bg-white/15 ring-4 ${style.ring} flex items-center justify-center backdrop-blur-sm`}>
              <span className="text-5xl font-black" data-testid="code-quality-letter">{data.letter}</span>
            </div>
            <div className="mt-3 text-2xl font-bold" data-testid="code-quality-score">{data.score}</div>
            <div className="text-xs opacity-80">/ 100</div>
          </div>

          <div className="lg:col-span-9 p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Santé du code backend</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {totalFns} fonctions analysées · CC moyen <span className="font-semibold">{data.kpis.cc_avg}</span>
                  {data._cache_hit && <span className="ml-2 text-slate-400">· (cache)</span>}
                </p>
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={() => fetchScore(true)} disabled={refreshing}
                className="text-xs"
                data-testid="code-quality-refresh-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                Recalculer
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <KpiCell label="CC max" value={data.kpis.cc_max}
                tone={data.kpis.cc_max > 30 ? 'danger' : data.kpis.cc_max > 20 ? 'warning' : 'good'}
                testid="code-quality-cc-max" />
              <KpiCell label="Grade D+" value={data.kpis.grade_d_plus}
                hint={`${((data.kpis.grade_d_plus / totalFns) * 100).toFixed(1)}%`}
                tone={data.kpis.grade_d_plus > 0 ? 'warning' : 'good'}
                testid="code-quality-grade-d" />
              <KpiCell label="Fichiers > 1500 LOC" value={data.kpis.huge_files}
                tone={data.kpis.huge_files > 10 ? 'warning' : 'good'}
                testid="code-quality-huge-files" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-slate-600">Distribution par grade</span>
                <span className="text-xs text-slate-400">{totalFns} fonctions</span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100" data-testid="code-quality-distribution">
                {['A', 'B', 'C', 'D', 'E', 'F'].map((g) => {
                  const count = data.distribution?.[g] || 0;
                  const pct = (count / totalFns) * 100;
                  if (pct < 0.1) return null;
                  return (
                    <div key={g} className={BAR_COLORS[g]} style={{ width: `${pct}%` }}
                      title={`${g} : ${count} fonctions (${pct.toFixed(1)}%)`} />
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs">
                {['A', 'B', 'C', 'D', 'E', 'F'].map((g) => {
                  const count = data.distribution?.[g] || 0;
                  if (count === 0) return null;
                  return (
                    <span key={g} className="flex items-center gap-1 text-slate-600">
                      <span className={`w-2 h-2 rounded-full ${BAR_COLORS[g]}`} />
                      <span className="font-semibold">{g}</span>
                      <span className="text-slate-400">{count}</span>
                    </span>
                  );
                })}
              </div>
            </div>

            {data.top_offenders && data.top_offenders.length > 0 && (
              <div className={`${style.light} rounded-lg p-3`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <TrendingUp className={`h-3.5 w-3.5 ${style.text}`} />
                  <span className={`text-xs font-semibold ${style.text}`}>
                    Top fonctions à refactorer (CC ≥ 31)
                  </span>
                </div>
                <ul className="text-xs text-slate-700 space-y-1">
                  {data.top_offenders.slice(0, 3).map((o, i) => (
                    <li key={i} className="flex items-center justify-between">
                      <span className="truncate font-mono">{o.function}</span>
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-white text-xs font-semibold">
                        {o.grade} ({o.complexity})
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {onViewDetails && (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={onViewDetails}
                  className="text-xs" data-testid="code-quality-details-btn">
                  Voir détails Code Health
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiCell({ label, value, hint, tone = 'neutral', testid }) {
  const toneStyles = {
    good: 'border-emerald-200 bg-emerald-50',
    warning: 'border-orange-200 bg-orange-50',
    danger: 'border-red-200 bg-red-50',
    neutral: 'border-slate-200 bg-slate-50'
  };
  const valueColor = {
    good: 'text-emerald-700', warning: 'text-orange-700',
    danger: 'text-red-700', neutral: 'text-slate-700'
  }[tone];

  return (
    <div className={`rounded-lg border p-3 ${toneStyles[tone]}`} data-testid={testid}>
      <div className="text-xs text-slate-500 font-medium">{label}</div>
      <div className={`text-2xl font-bold ${valueColor} mt-0.5`}>{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}
